import React, { useState } from 'react';
import { FileText, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Note category styles
const CATEGORY_STYLES = {
  strengths: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  improvements: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  goals: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  general: 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
};

/**
 * Notes Section - Add and display review notes
 */
export default function NotesSection({ reviewData, onNoteAdded, onNoteDeleted }) {
  const [newNote, setNewNote] = useState('');
  const [noteCategory, setNoteCategory] = useState('general');
  const [deleting, setDeleting] = useState(null);

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    try {
      const res = await api.post('/admin/performance/reviews/notes', {
        review_id: reviewData.id,
        notes: newNote,
        category: noteCategory
      });
      if (res.data.success) {
        onNoteAdded(res.data.note);
        setNewNote('');
        toast.success('Note added');
      }
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    setDeleting(noteId);
    try {
      const res = await api.delete(`/admin/performance/reviews/notes/${reviewData.id}/${noteId}`);
      if (res.data.success) {
        onNoteDeleted(noteId);
        toast.success('Note deleted');
      }
    } catch (err) {
      toast.error('Failed to delete note');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5" />
        Review Notes
      </h3>
      
      {/* Existing Notes */}
      {reviewData.notes && reviewData.notes.length > 0 && (
        <div className="space-y-3 mb-4">
          {reviewData.notes.map((note) => (
            <div key={note.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg group">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_STYLES[note.category] || CATEGORY_STYLES.general}`}>
                      {note.category}
                    </span>
                    <span className="text-xs text-gray-400">{new Date(note.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">{note.content}</p>
                </div>
                <button
                  onClick={() => handleDeleteNote(note.id)}
                  disabled={deleting === note.id}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                  title="Delete note"
                >
                  <Trash2 className={`w-4 h-4 ${deleting === note.id ? 'animate-pulse' : ''}`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Add Note */}
      <div className="flex gap-2">
        <select
          value={noteCategory}
          onChange={(e) => setNoteCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="general">General</option>
          <option value="strengths">Strengths</option>
          <option value="improvements">Improvements</option>
          <option value="goals">Goals</option>
        </select>
        <Input
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note..."
          className="flex-1"
          onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
        />
        <Button onClick={handleAddNote} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-note-btn">
          Add Note
        </Button>
      </div>
    </div>
  );
}
